import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { Plus, Printer, Send, CheckCircle2, XCircle, PenTool, Type, RotateCcw, FileSignature } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/lib/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRAND_LOGO_URL = "https://customer-assets.emergentagent.com/job_iv-appointments/artifacts/195kaj6c_Eliteiv%20logo%20No%20Number%20or%20website.png";

const statusBadge = (status) => ({
  draft: { label: "Draft", cls: "border-muted-foreground/30 text-muted-foreground bg-muted/20" },
  pending: { label: "Awaiting MD Signature", cls: "border-amber-500/40 text-amber-300 bg-amber-500/10" },
  signed: { label: "Signed & Approved", cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" },
  rejected: { label: "Returned by MD", cls: "border-rose-500/40 text-rose-300 bg-rose-500/10" },
}[status] || { label: status, cls: "border-border/40 text-muted-foreground" });

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const safe = (v, fb = '—') => (v === null || v === undefined || v === '' ? fb : esc(v));

// ==================== PRINT (Letterhead) ====================
export const printProgressNote = (note, patient) => {
  if (!note) return;
  const today = format(new Date(), 'MMM d, yyyy h:mm a');
  const p = patient || {};
  const pname = `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown Patient';
  const statusLabel = { draft: 'DRAFT — NOT SUBMITTED', pending: 'PENDING PHYSICIAN SIGNATURE', signed: 'SIGNED & APPROVED', rejected: 'RETURNED — REVISIONS NEEDED' }[note.status] || note.status;
  const signatureBlock = note.status === 'signed'
    ? (note.signature_type === 'drawn'
        ? `<img src="${note.signature_data}" style="height:70px;border-bottom:1px solid #333;" />`
        : `<div style="font-family:'Brush Script MT',cursive;font-size:30px;color:#111;border-bottom:1px solid #333;padding-bottom:6px;">${esc(note.signed_name)}</div>`)
    : `<div style="border-bottom:1px solid #ccc;height:44px;"></div>`;

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Progress Note – ${esc(pname)}</title>
<style>
  @page { size: letter; margin: 0.5in; }
  * { box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #111; font-size: 11pt; margin: 0; }
  .header { border-bottom: 3px solid #b8860b; padding-bottom: 12px; margin-bottom: 18px; display: flex; justify-content: space-between; align-items: center; }
  .logo { height: 56px; object-fit: contain; }
  .brand-sub { font-size: 8pt; letter-spacing: 3px; color: #666; text-transform: uppercase; margin-top: 4px; }
  .status { display:inline-block; padding: 4px 12px; border-radius: 999px; font-size: 8.5pt; font-weight:700; letter-spacing: 0.08em; text-transform:uppercase; }
  .status.signed { background:#e8f5ec; color:#1d7a43; }
  .status.pending { background:#fdf3dc; color:#8a6300; }
  .status.draft { background:#eee; color:#666; }
  .status.rejected { background:#fbe7e4; color:#a4362a; }
  .printed { font-size: 9pt; color: #555; text-align: right; }
  h1 { font-size: 16pt; margin: 14px 0 4px 0; color: #222; }
  .sub { color: #555; font-size: 10pt; }
  h2 { font-size: 11pt; text-transform: uppercase; letter-spacing: 1.5px; color: #b8860b; margin: 18px 0 6px 0; border-bottom: 1px solid #e2c275; padding-bottom: 3px; }
  .box { font-size: 10.5pt; line-height:1.6; white-space: pre-wrap; padding: 4px 2px 12px 2px; color:#222; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 18px; margin-bottom: 8px; }
  .field .label { color: #666; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.5px; }
  .sig-row { margin-top: 30px; max-width: 320px; }
  .sig-name { font-size: 10pt; margin-top: 6px; }
  .footer { margin-top: 24px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 8pt; color: #777; text-align: center; }
  @media print { .no-print { display: none; } }
</style></head><body>
<div class="header">
  <div>
    <img class="logo" src="${BRAND_LOGO_URL}" alt="EliteIV" />
    <div class="brand-sub">EMR · Progress Note</div>
  </div>
  <div class="printed">
    Printed: ${esc(today)}<br/>
    <span class="status ${note.status}">${esc(statusLabel)}</span>
  </div>
</div>

<h1>${esc(pname)}</h1>
<div class="sub">${p.date_of_birth ? 'DOB ' + esc(p.date_of_birth) + ' · ' : ''}Note created ${note.created_at ? esc(format(parseISO(note.created_at), 'MMM d, yyyy')) : ''}</div>

<div class="grid" style="margin-top:14px;">
  <div class="field"><div class="label">Authored By</div><div>${safe(note.author_name)}</div></div>
  <div class="field"><div class="label">Assigned Physician</div><div>${safe(note.physician_name)}</div></div>
</div>

<h2>Subjective</h2><div class="box">${safe(note.subjective, 'No subjective findings documented.')}</div>
<h2>Objective</h2><div class="box">${safe(note.objective, 'No objective findings documented.')}</div>
<h2>Assessment</h2><div class="box">${safe(note.assessment, 'No assessment documented.')}</div>
<h2>Plan</h2><div class="box">${safe(note.plan, 'No plan documented.')}</div>
${note.additional_notes ? `<h2>Additional Notes</h2><div class="box">${esc(note.additional_notes)}</div>` : ''}

<div class="sig-row">
  ${signatureBlock}
  <div class="sig-name"><strong>${safe(note.signed_name || note.physician_name)}</strong>${note.signed_credentials ? ', ' + esc(note.signed_credentials) : ''}</div>
  <div class="sig-name" style="color:#777;">${note.signed_at ? 'Signed electronically ' + esc(format(parseISO(note.signed_at), "MMM d, yyyy 'at' h:mm a")) : 'Awaiting electronic signature'}</div>
</div>

<div class="footer">
  <div><b>CONFIDENTIAL – PROTECTED HEALTH INFORMATION</b></div>
  <div>This document contains information protected under HIPAA. Unauthorized disclosure is prohibited. EliteIV Lounge.</div>
</div>
<script>window.onload = function(){ setTimeout(function(){ window.print(); }, 350); };</script>
</body></html>`;

  const w = window.open('', '_blank', 'width=900,height=1200');
  if (!w) { toast.error('Pop-up blocked. Please allow pop-ups to print.'); return; }
  w.document.open();
  w.document.write(html);
  w.document.close();
};

// ==================== SIGNATURE PAD ====================
const SignaturePad = ({ onChange }) => {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const start = (e) => { isDrawing.current = true; const { x, y } = pos(e); const ctx = canvasRef.current.getContext('2d'); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => {
    if (!isDrawing.current) return;
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y); ctx.strokeStyle = '#1a1a1a'; ctx.lineWidth = 2.2; ctx.lineCap = 'round'; ctx.stroke();
  };
  const end = () => { if (!isDrawing.current) return; isDrawing.current = false; onChange(canvasRef.current.toDataURL('image/png')); };
  const clear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); onChange(''); };

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={480}
        height={140}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        className="w-full bg-white rounded-md border border-border/50 cursor-crosshair touch-none"
        data-testid="pn-signature-canvas"
      />
      <Button type="button" variant="ghost" size="sm" onClick={clear} data-testid="pn-signature-clear-btn"><RotateCcw className="w-3.5 h-3.5 mr-1" />Clear</Button>
    </div>
  );
};

// ==================== SOAP FORM ====================
const ProgressNoteForm = ({ initial, patients, fixedPatientId, onSaved, onClose }) => {
  const [physicians, setPhysicians] = useState([]);
  const [form, setForm] = useState(initial ? {
    patient_id: initial.patient_id, physician_id: initial.physician_id,
    subjective: initial.subjective || '', objective: initial.objective || '', assessment: initial.assessment || '',
    plan: initial.plan || '', additional_notes: initial.additional_notes || '',
  } : { patient_id: fixedPatientId || '', physician_id: '', subjective: '', objective: '', assessment: '', plan: '', additional_notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { axios.get(`${API}/physicians`).then((r) => setPhysicians(r.data)).catch(() => toast.error('Could not load physicians')); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const persist = async () => {
    if (!form.patient_id) { toast.error('Select a patient'); return null; }
    if (!form.physician_id) { toast.error('Select a physician to send this note to'); return null; }
    setSaving(true);
    try {
      const res = initial ? await axios.put(`${API}/progress-notes/${initial.id}`, form) : await axios.post(`${API}/progress-notes`, form);
      return res.data;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); return null; }
    finally { setSaving(false); }
  };

  const saveDraft = async () => { const saved = await persist(); if (saved) { toast.success('Draft saved'); onSaved(saved); } };
  const submitForApproval = async () => {
    const saved = await persist();
    if (!saved) return;
    try {
      const res = await axios.post(`${API}/progress-notes/${saved.id}/submit`);
      toast.success('Sent to physician for signature');
      onSaved(res.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="progress-note-form">
      {!fixedPatientId && (
        <div className="space-y-2">
          <Label>Patient *</Label>
          <Select value={form.patient_id} onValueChange={(v) => set('patient_id', v)}>
            <SelectTrigger data-testid="pn-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
            <SelectContent>
              {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-2">
        <Label>Physician for Sign-Off *</Label>
        <Select value={form.physician_id} onValueChange={(v) => set('physician_id', v)}>
          <SelectTrigger data-testid="pn-physician-select"><SelectValue placeholder={physicians.length ? "Select physician" : "No physicians on file"} /></SelectTrigger>
          <SelectContent>
            {physicians.map((ph) => <SelectItem key={ph.id} value={ph.id}>{ph.name || ph.email}</SelectItem>)}
          </SelectContent>
        </Select>
        {physicians.length === 0 && <p className="text-[11px] text-amber-400/80">No physician accounts found. Ask an admin to add one under Users → Role: Physician.</p>}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Subjective</Label><Textarea rows={3} value={form.subjective} onChange={(e) => set('subjective', e.target.value)} placeholder="Patient-reported symptoms, history..." data-testid="pn-subjective" /></div>
        <div className="space-y-2"><Label>Objective</Label><Textarea rows={3} value={form.objective} onChange={(e) => set('objective', e.target.value)} placeholder="Vitals, exam findings..." data-testid="pn-objective" /></div>
        <div className="space-y-2"><Label>Assessment</Label><Textarea rows={3} value={form.assessment} onChange={(e) => set('assessment', e.target.value)} placeholder="Clinical impression..." data-testid="pn-assessment" /></div>
        <div className="space-y-2"><Label>Plan</Label><Textarea rows={3} value={form.plan} onChange={(e) => set('plan', e.target.value)} placeholder="Treatment plan, follow-up..." data-testid="pn-plan" /></div>
      </div>
      <div className="space-y-2"><Label>Additional Notes (free text)</Label><Textarea rows={2} value={form.additional_notes} onChange={(e) => set('additional_notes', e.target.value)} placeholder="Anything else worth documenting..." data-testid="pn-additional-notes" /></div>
      <div className="flex flex-wrap justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onClose} data-testid="pn-cancel-btn">Cancel</Button>
        <Button variant="outline" onClick={saveDraft} disabled={saving} data-testid="pn-save-draft-btn">Save Draft</Button>
        <Button onClick={submitForApproval} disabled={saving} className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 text-black" data-testid="pn-submit-btn"><Send className="w-4 h-4 mr-1" />Submit for MD Approval</Button>
      </div>
    </div>
  );
};

// ==================== DETAIL / VIEW ====================
const ProgressNoteDetail = ({ note, patient, onClose, onRefresh, onEdit, readOnly = false }) => {
  const badge = statusBadge(note.status);
  const [busy, setBusy] = useState(false);

  const resend = async () => {
    setBusy(true);
    try { await axios.post(`${API}/progress-notes/${note.id}/submit`); toast.success('Sent to physician'); onRefresh(); onClose(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this progress note?')) return;
    try { await axios.delete(`${API}/progress-notes/${note.id}`); toast.success('Deleted'); onRefresh(); onClose(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={!!note} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="progress-note-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-primary" />
            Progress Note — {patient ? `${patient.first_name} ${patient.last_name}` : 'Patient'}
          </DialogTitle>
        </DialogHeader>
        <Badge variant="outline" className={badge.cls}>{badge.label}</Badge>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
            <div>Authored by <span className="text-foreground">{note.author_name || '—'}</span></div>
            <div>Physician <span className="text-foreground">{note.physician_name || '—'}</span></div>
          </div>
          {[['Subjective', note.subjective], ['Objective', note.objective], ['Assessment', note.assessment], ['Plan', note.plan], ['Additional Notes', note.additional_notes]].map(([label, val]) => (
            <div key={label}>
              <p className="text-[10px] uppercase tracking-widest text-primary/70">{label}</p>
              <p className="text-sm whitespace-pre-wrap">{val || '—'}</p>
            </div>
          ))}
          {note.status === 'rejected' && note.rejection_reason && (
            <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-md text-rose-300 text-xs" data-testid="pn-rejection-reason">
              <strong>MD feedback:</strong> {note.rejection_reason}
            </div>
          )}
          {note.status === 'signed' && (
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-md text-xs">
              Signed by <strong>{note.signed_name}</strong>{note.signed_credentials ? `, ${note.signed_credentials}` : ''} on {note.signed_at ? format(parseISO(note.signed_at), "MMM d, yyyy 'at' h:mm a") : ''}
            </div>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => printProgressNote(note, patient)} data-testid="pn-print-btn"><Printer className="w-4 h-4 mr-1" />Print</Button>
          {!readOnly && (note.status === 'draft' || note.status === 'rejected') && (
            <>
              <Button variant="outline" size="sm" onClick={() => onEdit(note)} data-testid="pn-edit-btn">Edit</Button>
              <Button size="sm" onClick={resend} disabled={busy} className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 text-black" data-testid="pn-resubmit-btn"><Send className="w-4 h-4 mr-1" />{note.status === 'rejected' ? 'Resubmit' : 'Submit for MD Approval'}</Button>
              <Button variant="ghost" size="sm" className="text-destructive" onClick={remove} data-testid="pn-delete-btn">Delete</Button>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="pn-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== TOP-LEVEL QUEUE (Nav Tab) ====================
export const ProgressNotesQueue = ({ patients }) => {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailNote, setDetailNote] = useState(null);
  const [editNote, setEditNote] = useState(null);

  const fetchNotes = useCallback(async () => {
    try { const res = await axios.get(`${API}/progress-notes`); setNotes(res.data); }
    catch (err) { toast.error('Failed to load progress notes'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const isPhysician = user?.role === 'physician';
  const isManagement = user?.role === 'management';
  const canAuthor = !isPhysician && !isManagement;
  const filtered = filter === 'all' ? notes : notes.filter((n) => n.status === filter);
  const patientName = (id) => { const p = patients.find((x) => x.id === id); return p ? `${p.first_name} ${p.last_name}` : 'Unknown patient'; };
  const counts = notes.reduce((acc, n) => { acc[n.status] = (acc[n.status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-4" data-testid="progress-notes-queue">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><FileSignature className="w-5 h-5 text-primary" />Progress Notes</h2>
          <p className="text-xs text-muted-foreground">{isPhysician ? 'Notes awaiting your review and electronic signature.' : isManagement ? 'Read-only view — check whether a physician has signed off.' : 'SOAP documentation routed to a physician for sign-off.'}</p>
        </div>
        {canAuthor && <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="pn-new-btn"><Plus className="w-4 h-4 mr-1" />New Progress Note</Button>}
      </div>
      <div className="flex gap-2 flex-wrap">
        {['all', 'draft', 'pending', 'signed', 'rejected'].map((s) => (
          <button key={s} type="button" onClick={() => setFilter(s)} data-testid={`pn-filter-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filter === s ? 'bg-primary/15 border-primary/50 text-primary' : 'border-border/50 text-muted-foreground hover:bg-muted/30'}`}>
            {s === 'all' ? `All (${notes.length})` : `${statusBadge(s).label} (${counts[s] || 0})`}
          </button>
        ))}
      </div>
      {loading ? <p className="text-muted-foreground text-sm">Loading…</p> : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center" data-testid="pn-empty-state">No progress notes {filter !== 'all' ? `in "${filter}"` : 'yet'}.</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((n) => {
            const badge = statusBadge(n.status);
            return (
              <button key={n.id} type="button" onClick={() => setDetailNote(n)} data-testid={`pn-row-${n.id}`}
                className="w-full text-left p-3 bg-muted/30 hover:bg-primary/10 border border-transparent hover:border-primary/40 rounded-lg transition-all flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{patientName(n.patient_id)}</p>
                  <p className="text-xs text-muted-foreground truncate">{n.physician_name || 'Physician unassigned'} · {n.created_at ? format(parseISO(n.created_at), 'MMM d, yyyy') : ''}</p>
                </div>
                <Badge variant="outline" className={`text-[10px] shrink-0 ${badge.cls}`}>{badge.label}</Badge>
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Progress Note</DialogTitle></DialogHeader>
          <ProgressNoteForm patients={patients} onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); fetchNotes(); }} />
        </DialogContent>
      </Dialog>

      {editNote && (
        <Dialog open={!!editNote} onOpenChange={(o) => { if (!o) setEditNote(null); }}>
          <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit Progress Note</DialogTitle></DialogHeader>
            <ProgressNoteForm initial={editNote} patients={patients} onClose={() => setEditNote(null)} onSaved={() => { setEditNote(null); setDetailNote(null); fetchNotes(); }} />
          </DialogContent>
        </Dialog>
      )}

      {detailNote && (
        <ProgressNoteDetail
          note={detailNote}
          patient={patients.find((p) => p.id === detailNote.patient_id)}
          onClose={() => setDetailNote(null)}
          onRefresh={fetchNotes}
          onEdit={(n) => { setDetailNote(null); setEditNote(n); }}
          readOnly={isManagement}
        />
      )}
    </div>
  );
};

// ==================== PATIENT CHART TAB ====================
export const PatientProgressNotesTab = ({ patientId, patient }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailNote, setDetailNote] = useState(null);
  const [editNote, setEditNote] = useState(null);

  const fetchNotes = useCallback(async () => {
    try { const res = await axios.get(`${API}/patients/${patientId}/progress-notes`); setNotes(res.data); }
    catch (err) { toast.error('Failed to load progress notes'); }
    finally { setLoading(false); }
  }, [patientId]);
  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  return (
    <div className="space-y-3" data-testid="patient-progress-notes-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">SOAP documentation sent to a physician for electronic sign-off.</p>
        <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)} data-testid="pn-patient-new-btn"><Plus className="w-4 h-4 mr-1" />New Progress Note</Button>
      </div>
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : notes.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">No progress notes for this patient yet.</p>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => {
            const badge = statusBadge(n.status);
            return (
              <button key={n.id} type="button" onClick={() => setDetailNote(n)} data-testid={`pn-patient-row-${n.id}`}
                className="w-full text-left p-3 bg-muted/30 hover:bg-primary/10 border border-transparent hover:border-primary/40 rounded-lg transition-all flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{n.physician_name || 'Physician unassigned'}</p>
                  <p className="text-xs text-muted-foreground truncate">{n.created_at ? format(parseISO(n.created_at), 'MMM d, yyyy · h:mm a') : ''}</p>
                </div>
                <Badge variant="outline" className={`text-[10px] shrink-0 ${badge.cls}`}>{badge.label}</Badge>
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Progress Note</DialogTitle></DialogHeader>
          <ProgressNoteForm patients={[patient]} fixedPatientId={patientId} onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); fetchNotes(); }} />
        </DialogContent>
      </Dialog>

      {editNote && (
        <Dialog open={!!editNote} onOpenChange={(o) => { if (!o) setEditNote(null); }}>
          <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit Progress Note</DialogTitle></DialogHeader>
            <ProgressNoteForm initial={editNote} patients={[patient]} fixedPatientId={patientId} onClose={() => setEditNote(null)} onSaved={() => { setEditNote(null); setDetailNote(null); fetchNotes(); }} />
          </DialogContent>
        </Dialog>
      )}

      {detailNote && (
        <ProgressNoteDetail
          note={detailNote}
          patient={patient}
          onClose={() => setDetailNote(null)}
          onRefresh={fetchNotes}
          onEdit={(n) => { setDetailNote(null); setEditNote(n); }}
        />
      )}
    </div>
  );
};

// ==================== PHYSICIAN REVIEW PAGE (Emailed Link) ====================
export const ProgressNoteReviewPage = () => {
  const { token } = useParams();
  const { user } = useAuth();
  const [note, setNote] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signMode, setSignMode] = useState('typed');
  const [typedName, setTypedName] = useState('');
  const [credentials, setCredentials] = useState('');
  const [drawnData, setDrawnData] = useState('');
  const [agree, setAgree] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/progress-notes/by-token/${token}`);
      setNote(res.data);
      setTypedName(user?.name || '');
      const pRes = await axios.get(`${API}/patients/${res.data.patient_id}`);
      setPatient(pRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'This review link is invalid or has expired.');
    } finally { setLoading(false); }
  }, [token, user]);

  useEffect(() => { load(); }, [load]);

  const sign = async () => {
    const signature_data = signMode === 'typed' ? typedName.trim() : drawnData;
    if (!signature_data) { toast.error(signMode === 'typed' ? 'Type your full legal name to sign' : 'Please draw your signature'); return; }
    if (!agree) { toast.error('Please confirm you have reviewed and approve this note'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/progress-notes/${note.id}/sign`, {
        signature_type: signMode, signature_data, signed_name: typedName.trim() || user?.name || 'Physician', credentials: credentials.trim() || null,
      });
      setNote(res.data);
      toast.success('Note signed and approved. A copy has been sent to Admin and the assigned nurse.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to sign note'); }
    finally { setSubmitting(false); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) { toast.error('Please explain what needs to change'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/progress-notes/${note.id}/reject`, { reason: rejectReason.trim() });
      setNote(res.data);
      toast.success('Note returned to the sender with your feedback.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to return note'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><p className="text-primary">Loading progress note…</p></div>;

  if (error) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="max-w-md gold-border">
        <CardContent className="p-8 text-center space-y-3">
          <XCircle className="w-12 h-12 mx-auto text-destructive" />
          <h2 className="text-xl font-semibold">Access Denied</h2>
          <p className="text-sm text-muted-foreground" data-testid="pn-review-error">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  const pname = patient ? `${patient.first_name} ${patient.last_name}` : 'Patient';
  const badge = statusBadge(note.status);

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-gradient-to-r from-black via-zinc-950 to-black border-b border-primary/30">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/eliteiv-logo.png" alt="EliteIV" className="w-10 h-10 object-contain" />
            <div>
              <h1 className="text-xl brand-heading"><span className="gold-gradient-text">Elite</span><span className="text-muted-foreground">IV</span></h1>
              <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mt-0.5">Physician Review</p>
            </div>
          </div>
          <Badge variant="outline" className={badge.cls} data-testid="pn-review-status">{badge.label}</Badge>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <Card className="gold-border bg-card/60">
          <CardContent className="p-6 space-y-1">
            <p className="text-xs uppercase tracking-widest text-primary/70">Progress Note For</p>
            <h2 className="text-2xl brand-heading gold-gradient-text">{pname}</h2>
            {patient?.date_of_birth && <p className="text-sm text-muted-foreground">DOB {patient.date_of_birth}</p>}
            <p className="text-sm text-muted-foreground mt-2">Authored by {note.author_name} · {note.created_at ? format(parseISO(note.created_at), 'MMM d, yyyy') : ''}</p>
          </CardContent>
        </Card>

        <Card className="bg-card/60">
          <CardContent className="p-6 space-y-4">
            {[['Subjective', note.subjective], ['Objective', note.objective], ['Assessment', note.assessment], ['Plan', note.plan], ['Additional Notes', note.additional_notes]].map(([label, val]) => (
              <div key={label}>
                <p className="text-xs font-semibold uppercase tracking-widest text-primary border-b border-primary/20 pb-2 mb-2">{label}</p>
                <p className="text-sm whitespace-pre-wrap text-foreground/90">{val || '—'}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        {note.status === 'signed' && (
          <Card className="bg-emerald-500/10 border-emerald-500/30">
            <CardContent className="p-6 text-center space-y-2">
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400" />
              <p className="text-sm text-emerald-300">Signed by <strong>{note.signed_name}</strong>{note.signed_credentials ? `, ${note.signed_credentials}` : ''} on {note.signed_at ? format(parseISO(note.signed_at), "MMM d, yyyy 'at' h:mm a") : ''}</p>
              <Button variant="outline" size="sm" onClick={() => printProgressNote(note, patient)} data-testid="pn-review-print-btn"><Printer className="w-4 h-4 mr-1" />Print Signed Note</Button>
            </CardContent>
          </Card>
        )}

        {note.status === 'rejected' && (
          <Card className="bg-rose-500/10 border-rose-500/30">
            <CardContent className="p-6 text-sm text-rose-300"><strong>You returned this note</strong> with feedback: {note.rejection_reason}</CardContent>
          </Card>
        )}

        {note.status === 'pending' && (
          <Card className="gold-border bg-card/60">
            <CardContent className="p-6 space-y-5">
              <h3 className="text-sm font-semibold uppercase tracking-widest text-primary">Electronic Signature</h3>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={signMode === 'typed' ? 'default' : 'outline'} onClick={() => setSignMode('typed')} data-testid="pn-sign-mode-typed"><Type className="w-4 h-4 mr-1" />Type Signature</Button>
                <Button type="button" size="sm" variant={signMode === 'drawn' ? 'default' : 'outline'} onClick={() => setSignMode('drawn')} data-testid="pn-sign-mode-drawn"><PenTool className="w-4 h-4 mr-1" />Draw Signature</Button>
              </div>
              {signMode === 'typed' ? (
                <div className="space-y-2"><Label>Full Legal Name *</Label><Input value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Dr. Jane Smith" data-testid="pn-typed-name" /></div>
              ) : (
                <div className="space-y-2"><Label>Draw Your Signature *</Label><SignaturePad onChange={setDrawnData} /></div>
              )}
              <div className="space-y-2"><Label>Credentials (optional)</Label><Input value={credentials} onChange={(e) => setCredentials(e.target.value)} placeholder="MD, DO, NP…" data-testid="pn-credentials" /></div>
              <div className="flex items-start gap-3 p-3 bg-muted/20 border border-border/40 rounded-md">
                <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid="pn-agree-checkbox" className="mt-0.5" />
                <Label className="text-sm leading-relaxed">I have reviewed this documentation and approve it as accurate and complete.</Label>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button onClick={sign} disabled={submitting} className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 text-black font-semibold" data-testid="pn-sign-submit-btn"><CheckCircle2 className="w-4 h-4 mr-1" />{submitting ? 'Signing…' : 'Sign & Approve'}</Button>
                <Button variant="outline" onClick={() => setShowReject((v) => !v)} data-testid="pn-toggle-reject-btn"><XCircle className="w-4 h-4 mr-1" />Return for Revisions</Button>
              </div>
              {showReject && (
                <div className="space-y-2 pt-2 border-t border-border/40">
                  <Label>What needs to change?</Label>
                  <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Explain what the author should revise..." data-testid="pn-reject-reason" />
                  <Button variant="destructive" size="sm" onClick={reject} disabled={submitting} data-testid="pn-reject-submit-btn">Return Note</Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};
